import { useState, useRef, useEffect } from 'react';

export default function Portfolio() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const projects = [
    {
      title: 'Modern Estate',
      category: 'Custom Build',
      image: 'https://public.readdy.ai/ai/img_res/1fd7adc24191131ab0187b9e5516d5af.jpg',
    },
    {
      title: 'Penthouse Suite',
      category: 'Luxury Renovation',
      image: 'https://readdy.ai/api/search-image?query=luxurious%20penthouse%20interior%20with%20panoramic%20city%20skyline%20views%20through%20floor-to-ceiling%20windows%2C%20designer%20furniture%2C%20marble%20flooring%2C%20contemporary%20art%20pieces%2C%20sophisticated%20lighting%20design%2C%20professional%20interior%20photography%20with%20rich%20textures%20and%20elegant%20color%20palette%20at%20dusk&width=1200&height=900&seq=portfolio-penthouse-002&orientation=landscape',
    },
    {
      title: 'Coastal Villa',
      category: 'Custom Build',
      image: 'https://readdy.ai/api/search-image?query=breathtaking%20coastal%20luxury%20villa%20with%20white%20modern%20architecture%2C%20expansive%20ocean%20views%2C%20infinity%20pool%20merging%20with%20horizon%2C%20palm%20trees%2C%20outdoor%20living%20spaces%2C%20Mediterranean-inspired%20design%20elements%2C%20professional%20architectural%20photography%20with%20azure%20blue%20water%20and%20clear%20sky&width=900&height=1200&seq=portfolio-coastal-villa-003&orientation=portrait',
    },
    {
      title: 'Master Suite',
      category: 'Renovation',
      image: 'https://readdy.ai/api/search-image?query=opulent%20master%20bedroom%20suite%20with%20king-size%20bed%2C%20tufted%20headboard%2C%20crystal%20chandelier%2C%20walk-in%20closet%20visible%20through%20glass%20doors%2C%20spa-like%20ensuite%20bathroom%2C%20floor-to-ceiling%20windows%2C%20luxury%20textiles%20and%20finishes%2C%20professional%20interior%20photography%20with%20soft%20romantic%20lighting&width=1200&height=900&seq=portfolio-master-suite-004&orientation=landscape',
    },
    {
      title: 'Gourmet Kitchen',
      category: 'Renovation',
      image: 'https://readdy.ai/api/search-image?query=spectacular%20gourmet%20kitchen%20with%20massive%20marble%20island%2C%20professional-grade%20stainless%20steel%20appliances%2C%20custom%20cabinetry%2C%20wine%20cellar%2C%20breakfast%20nook%20with%20bay%20windows%2C%20pendant%20lighting%2C%20hardwood%20floors%2C%20professional%20interior%20photography%20showcasing%20culinary%20luxury%20with%20warm%20inviting%20ambiance&width=900&height=1200&seq=portfolio-gourmet-kitchen-005&orientation=portrait',
    },
    {
      title: 'Mountain Retreat',
      category: 'Custom Build',
      image: 'https://readdy.ai/api/search-image?query=luxurious%20mountain%20retreat%20with%20timber%20and%20stone%20exterior%2C%20large%20windows%20framing%20mountain%20vistas%2C%20outdoor%20deck%20with%20fire%20pit%2C%20modern%20rustic%20architecture%20blending%20with%20natural%20surroundings%2C%20professional%20architectural%20photography%20during%20sunset%20with%20dramatic%20mountain%20backdrop&width=1200&height=900&seq=portfolio-mountain-retreat-006&orientation=landscape',
    },
  ];

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!scrollContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.touches[0].pageX - scrollContainerRef.current.offsetLeft);
    setScrollLeft(scrollContainerRef.current.scrollLeft);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !scrollContainerRef.current) return;
    const x = e.touches[0].pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollContainerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;
      const scrollPosition = scrollContainerRef.current.scrollLeft;
      const itemWidth = scrollContainerRef.current.offsetWidth * 0.8;
      const newIndex = Math.round(scrollPosition / itemWidth);
      setActiveIndex(newIndex);
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const scrollToIndex = (index: number) => {
    if (!scrollContainerRef.current) return;
    const itemWidth = scrollContainerRef.current.offsetWidth * 0.8;
    scrollContainerRef.current.scrollTo({
      left: index * itemWidth,
      behavior: 'smooth',
    });
    setActiveIndex(index);
  };

  return (
    <section id="portfolio" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-yellow-600">Portfolio</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Explore our collection of exceptional projects that showcase our commitment to luxury and excellence
          </p>
        </div>

        <div className="relative">
          <div
            ref={scrollContainerRef}
            className="flex gap-6 overflow-x-auto scrollbar-hide snap-x snap-mandatory cursor-grab active:cursor-grabbing"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {projects.map((project, index) => (
              <div
                key={index}
                className="flex-shrink-0 w-[80%] md:w-[45%] lg:w-[30%] snap-center"
              >
                <div className="group relative h-[500px] rounded-lg overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                    draggable="false"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    <span className="inline-block px-3 py-1 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black text-xs font-bold rounded-full mb-3">
                      {project.category}
                    </span>
                    <h3 className="text-2xl font-bold mb-2">{project.title}</h3>
                    <div className="h-1 w-16 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-full"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center gap-2 mt-8">
            {projects.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                className={`w-3 h-3 rounded-full transition-all cursor-pointer ${
                  index === activeIndex ? 'bg-yellow-500 w-8' : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to project ${index + 1}`}
              />
            ))}
          </div>
        </div>

        <div className="text-center mt-12">
          <p className="text-gray-500 text-sm">
            <i className="ri-drag-move-line mr-2"></i>
            Drag or swipe to explore more projects
          </p>
        </div>
      </div>
    </section>
  );
}
